/**
 * SEO — imperative <head> updater.
 *
 * Uses useEffect + direct DOM to set <title>, meta description, canonical
 * link, and Open Graph / Twitter card tags. Avoids the react-helmet-async
 * dependency (which requires a Provider at the root and adds ~10kb).
 *
 * Renders nothing. All effects clean up on unmount so a route change
 * back to a page without SEO doesn't leave stale tags behind.
 */
import { useLayoutEffect } from "react";

// Tags this component owns. Every effect on mount either finds and
// updates an existing tag or creates one; on unmount only the tags this
// instance created are removed (tags marked with data-seo-managed).
const MANAGED = "data-seo-managed";

function upsertMeta(kind, keyName, keyVal, content) {
  if (!content) return null;
  const sel = `meta[${kind}="${keyVal}"]`;
  let el = document.head.querySelector(sel);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(kind, keyVal);
    el.setAttribute(MANAGED, "1");
    document.head.appendChild(el);
    created = true;
  }
  el.setAttribute("content", content);
  return { el, created };
}

function upsertLink(rel, href) {
  if (!href) return null;
  const sel = `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  let created = false;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(MANAGED, "1");
    document.head.appendChild(el);
    created = true;
  }
  el.setAttribute("href", href);
  return { el, created };
}

export default function Seo({
  title,
  description,
  canonical,
  image,
  type = "website",
  siteName = "CasperDomains",
  twitterHandle = "",
  structuredData = null,
}) {
  // useLayoutEffect fires synchronously after DOM mutations but *before*
  // the browser paints — critical here so the tab title / OG tags never
  // "flash" from the index.html-baked fallback to the per-page value.
  useLayoutEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    // Absolute URL preferred for canonical + og:url. Falls back to the
    // current location so preview + prod both get valid URLs.
    const url = canonical || (typeof window !== "undefined" ? window.location.href : "");

    const created = [];
    const track = (r) => { if (r?.created) created.push(r.el); };

    track(upsertMeta("name", "name", "description", description));
    track(upsertLink("canonical", url));

    // Open Graph
    track(upsertMeta("property", "property", "og:title", title));
    track(upsertMeta("property", "property", "og:description", description));
    track(upsertMeta("property", "property", "og:type", type));
    track(upsertMeta("property", "property", "og:url", url));
    track(upsertMeta("property", "property", "og:site_name", siteName));
    if (image) track(upsertMeta("property", "property", "og:image", image));

    // Twitter Card
    track(upsertMeta("name", "name", "twitter:card", image ? "summary_large_image" : "summary"));
    track(upsertMeta("name", "name", "twitter:title", title));
    track(upsertMeta("name", "name", "twitter:description", description));
    if (image) track(upsertMeta("name", "name", "twitter:image", image));
    if (twitterHandle) track(upsertMeta("name", "name", "twitter:site", twitterHandle));

    // Structured data (JSON-LD). One script per SEO instance, replaced
    // fresh each render so stale data can't linger.
    let scriptEl = null;
    if (structuredData) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.setAttribute(MANAGED, "1");
      try {
        scriptEl.text = JSON.stringify(structuredData);
        document.head.appendChild(scriptEl);
      } catch {
        scriptEl = null;
      }
    }

    return () => {
      // Remove only what this instance created; leaves index.html-baked
      // tags (e.g. the site-wide fallback description) untouched.
      created.forEach((el) => el.remove());
      if (scriptEl) scriptEl.remove();
      if (title) document.title = prevTitle;
    };
  }, [title, description, canonical, image, type, siteName, twitterHandle, structuredData]);

  return null;
}
